import {AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, Input, ViewChild} from '@angular/core';
import {FormControl, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MatAutocompleteModule} from '@angular/material/autocomplete';
import {MatInputModule} from '@angular/material/input';
import {MatFormFieldControl} from '@angular/material/form-field';
import {mapBasertPåIndeks, nyArray} from "felles-ng2/støttefunksjoner/lister";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {Temporal} from "temporal-polyfill";
import {FormaterPlainTimePipe} from "felles-ng2/pipes/FormaterPlainTimePipe";
import {FormaterDurationPipe} from "felles-ng2/pipes/FormaterDurationPipe";
import {MatSelect, MatSelectModule} from "@angular/material/select";
import {TemporalFormatering} from "felles-ng2/tjenester/TemporalFormatering";
import {Subject} from "rxjs";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {MatFormFieldControlProxy} from "../../material/MatFormFieldControlProxy";
import {erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {TranslateModule} from "@ngx-translate/core";
import Duration = Temporal.Duration;
import PlainTime = Temporal.PlainTime;

const debug = initDebugger('VarighetMinutterVelgerKomponent');

/**
 * Input-felt for angivelse av varighet som lar bruker velge skrive inn klokkeslett
 * samt velge fra en nedtrekksliste med predefinerte klokkeslett.
 * <pre>
 * Dersom starttidspunkt-attributtet er angitt vil nedtrekksliste inneholder klokkeslett som starter fra angitt tidspunkt.
 *
 * Fungerer med Ng reactive form via ([(skjemakontroll)]="...").
 * Fungerer med <mat-form-field> fra Material.
 *
 * MERK at vi ikke implementerer ControlValueAccessor fra angular.forms, så komponenten vil ikke fungere med
 * [(ngModel)]="..." eller [formControl]="...".
 * </pre>
 */
@Component({
    selector: 'varighet-minutter-velger',
    templateUrl: 'varighet-minutter-velger.html',
    styleUrls: ['varighet-minutter-velger.scss'],
    imports: [
        FormsModule,
        MatInputModule,
        ReactiveFormsModule,
        MatAutocompleteModule,
        FormaterPlainTimePipe,
        FormaterDurationPipe,
        MatSelectModule,
        TranslateModule
    ],
    providers: [{
        provide: MatFormFieldControl,
        useFactory: (komponent: VarighetMinutterVelgerKomponent) => new MatFormFieldControlProxy(() => komponent.nedtrekksmeny, komponent.stateChanges),
        deps: [VarighetMinutterVelgerKomponent]
    }],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class VarighetMinutterVelgerKomponent implements AfterViewInit {

    @ViewChild('varighetvelger') private nedtrekksmeny!: MatSelect;

    @Input({required: true}) skjemakontroll!: FormControl<Duration | null>;
    @Input() placeholder = '';

    @Input() set starttidspunkt(tidspunkt: PlainTime) {
        this.setStarttidspunkt(tidspunkt);
    }

    varigheter: VarigheterInnslag[];

    private _starttidspunkt: PlainTime | null = null;
    private readonly stateChanges = new Subject<void>();

    constructor(private temporalFormateringTjeneste: TemporalFormatering, private destroyRef: DestroyRef) {
        this.varigheter = this.varigheter5MinIntervaller();
    }

    get starttidspunkt(): PlainTime | null {
        return this._starttidspunkt;
    }

    erValgt(a: Duration, b: Duration) {
        return a?.minutes === b?.minutes;
    }

    valgtVarighetSomKlokkeslett() {
        if (erNil(this.skjemakontroll.value) || erNil(this.starttidspunkt)) return null;
        return this.starttidspunkt.add(this.skjemakontroll.value);
    }

    ngAfterViewInit(): void {
        this.nedtrekksmeny.stateChanges
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => this.stateChanges.next())
    }

    //hjelpemetoder

    private setStarttidspunkt(tidspunkt: PlainTime) {
        this._starttidspunkt = tidspunkt;
        this.varigheter = this.varigheter5MinIntervaller(tidspunkt);
    }

    private varigheter5MinIntervaller(startKlokkeslett: PlainTime | null = null): VarigheterInnslag[] {
        const varigheter = nyArray(24 * 12) //en time innholder 12 fem-minutters-intervaller, ett døgn inneholder 24 timer
          .map(mapBasertPåIndeks(indeks => this.somVarigheterInnslag(indeks * 5, startKlokkeslett)))
          .filter((verdi, indeks) => indeks > 0); //fjerner første innslag som repr 0 minutter

        //legg til 1min og 2min som tilgjengelige varigheter før 5min
        return [
            this.somVarigheterInnslag(1, startKlokkeslett),
            this.somVarigheterInnslag(2, startKlokkeslett),
            ...varigheter
        ];
    }

    private somVarigheterInnslag(antallMinutter: number, startKlokkeslett: PlainTime | null): VarigheterInnslag {
        const varighet = Duration.from({minutes: antallMinutter});
        const klokkeslett = startKlokkeslett?.add(varighet) ?? null;
        return {varighet, antallMinutter, klokkeslett};
    }
}


export interface VarigheterInnslag {
    varighet: Duration;
    antallMinutter: number;
    klokkeslett: PlainTime | null;
}
